// A LINE'S ASSOCIATED ENTITIES ICON (D-154) — the Vehicle, Staff and
// Participant tags a line item is for, behind one icon at every step.
//
// Extracted from `EditableInvoiceForm.tsx`, where it was the editor's own
// button at AP/AR Approval and nowhere else; the read-only table showed the
// same tags as chips under the description instead. Both tables now render
// this, so the tags look and open the same way at every step:
//  - editable (Import Control, where the server lets the viewer write them —
//    D-216; AP/AR Approval until it merged into Import Control): the full
//    `AssociatedEntitiesEditor`;
//  - read-only (every other step): the same pop-up, listing the tags as text.
//
// Blue when the line has at least one active tag; a red border when one is
// unmatched and the viewer can fix it here (`../lib/required.ts`).
import { Caption1 } from '@fluentui/react-components'
import { LinkFilled, LinkRegular } from '@fluentui/react-icons'
import type { Invoice, InvoiceLineItem } from '../lib/types'
import type { LineIconState } from '../lib/required'
import { useLineItemPanelStyles } from '../lib/theme'
import { AssociatedEntitiesEditor } from './AssociatedEntitiesEditor'
import { LineItemPopover } from './LineItemIconButton'

/** The name on the icon, and the pop-up's heading. */
export const ASSOCIATED_ENTITIES_LABEL = 'Associated Entities'

interface Props {
  invoice: Invoice
  lineItem: InvoiceLineItem
  state: LineIconState
  /** The viewer may add, edit and remove tags here. */
  editable: boolean
  /** A line not saved yet has no id to hang a tag on. */
  unsaved?: boolean
  /** Fired after a tag is added, edited or removed. */
  onChanged?: () => void
  mountNode?: HTMLElement | null
  /** THE LINE-ITEM BULK ROW (D-242): list the tags of every one of these lines,
      one per entity, instead of `lineItem`'s — the tags a Combine copies onto
      its new line. Read-only. */
  lineItemIds?: readonly string[]
  /** The icon shows but does not open — the bulk row's Apply mode (D-242). */
  disabled?: boolean
}

export function AssociatedEntitiesButton({ invoice, lineItem, state, editable, unsaved, onChanged, mountNode, lineItemIds, disabled }: Props) {
  const ps = useLineItemPanelStyles()
  return (
    <LineItemPopover
      regularIcon={LinkRegular}
      filledIcon={LinkFilled}
      label={ASSOCIATED_ENTITIES_LABEL}
      filled={state.filled}
      requiredMissing={state.requiredMissing}
      mountNode={mountNode}
      disabled={disabled}
    >
      {unsaved ? (
        <div className={ps.root}>
          <Caption1 className={ps.note}>Save the invoice to tag entities on this line.</Caption1>
        </div>
      ) : (
        <AssociatedEntitiesEditor
          invoice={invoice}
          lineItem={lineItem}
          readOnly={!editable || lineItemIds !== undefined}
          onChanged={onChanged}
          childIds={lineItemIds}
        />
      )}
    </LineItemPopover>
  )
}
